import { ReadPersonDto, ReadPersonDefensorDto } from '@/api.types';

/** Referência mínima de perfil (usuário, colaborador, etc.) no formulário. */
export type ProfileRef = { id?: number; ativo?: boolean };

/**
 * Perfil presente no formulário: cadastro existente (`id`) ou marcado ativo.
 * Mesma regra de `edit.tsx` ao decidir se o perfil vai no payload (`!id && !ativo` → remove).
 */
export function profileHasPresence(profile?: ProfileRef): boolean {
  return !!(profile?.id || profile?.ativo);
}

export function shouldIncludeProfileInPayload(
  profile: ProfileRef | undefined,
  podeVer: boolean,
  tipoMatches: boolean,
): boolean {
  return podeVer && tipoMatches && profileHasPresence(profile);
}

/** Converte `null`/`undefined` em string vazia (API e inputs desabilitados). */
function str(value: unknown): string {
  return value == null ? '' : String(value);
}

function bool(value: unknown): boolean | undefined {
  if (value === null || value === undefined) return undefined;
  return value === true || value === 'true';
}

/** Perfis ausentes na API vêm como `null`; Zod `.optional()` só aceita `undefined`. */
function profileOrUndefined<T>(value: T | null | undefined): T | undefined {
  return value == null ? undefined : value;
}

/** Normaliza valores da API para o formulário (Zod não aceita `null` em `z.string()` / `z.boolean()`). */
export function normalizePersonFormFromApi(pessoa: ReadPersonDto): ReadPersonDto {
  return {
    ...pessoa,
    email: str(pessoa.email),
    emailInstitucional: str(pessoa.emailInstitucional),
    sobrenome: str(pessoa.sobrenome),
    dataNascimento: str(pessoa.dataNascimento),
    podeserresponsavel: bool(pessoa.podeserresponsavel) ?? false,
    endereco: pessoa.endereco
      ? {
          ...pessoa.endereco,
          cep: str(pessoa.endereco.cep),
          logradouro: str(pessoa.endereco.logradouro),
          bairro: str(pessoa.endereco.bairro),
          cidade: str(pessoa.endereco.cidade),
          estado: str(pessoa.endereco.estado),
          complemento: str(pessoa.endereco.complemento),
          numero: pessoa.endereco.numero ?? undefined,
        }
      : pessoa.endereco,
    telefone: pessoa.telefone
      ? { ...pessoa.telefone, numero: str(pessoa.telefone.numero) }
      : pessoa.telefone,
    celular: pessoa.celular
      ? { ...pessoa.celular, numero: str(pessoa.celular.numero) }
      : pessoa.celular,
    usuario: profileOrUndefined(
      pessoa.usuario
        ? {
            ...pessoa.usuario,
            username: str(pessoa.usuario.username),
            funcao: str(pessoa.usuario.funcao),
            corfundotabela: str(pessoa.usuario.corfundotabela) || '#e5e5e5',
            corletratabela: str(pessoa.usuario.corletratabela) || '#000000',
          }
        : null,
    ),
    colaborador: profileOrUndefined(
      pessoa.colaborador
        ? {
            ...pessoa.colaborador,
            orgao_origem: str(pessoa.colaborador.orgao_origem),
            anotacao: str(pessoa.colaborador.anotacao),
            data_cessao: str(pessoa.colaborador.data_cessao),
            data_saida: str(pessoa.colaborador.data_saida),
            vinculo_direto: bool(pessoa.colaborador.vinculo_direto) ?? false,
            cessao_onerosa: bool(pessoa.colaborador.cessao_onerosa) ?? false,
          }
        : null,
    ),
    defensor: profileOrUndefined(
      pessoa.defensor
        ? ({
            ...pessoa.defensor,
            categoria: str(pessoa.defensor.categoria),
          } as ReadPersonDefensorDto)
        : null,
    ),
    estagiario: profileOrUndefined(
      pessoa.estagiario
        ? {
            ...pessoa.estagiario,
            anotacao: str(pessoa.estagiario.anotacao),
            estagios: pessoa.estagiario.estagios?.map((estagio) => ({
              ...estagio,
              fim_semana: bool(estagio.fim_semana) ?? false,
            })),
          }
        : null,
    ),
  };
}
